import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import type {
	OpenParams,
	Panel,
	PreviewPanelsAction,
	PreviewPanelsApiState,
	SwapParams,
} from '../types';

import { doPreviewPanelTypesMatch } from './utils';

const noop = (props?: any) => {};

const initialState: PreviewPanelsApiState = {
	// Configured when opening a panel.
	panel: null,
	width: 400,
	onOpen: noop,
	onClose: noop,
};

export const PreviewPanelsContext = createContext<
	| {
			state: PreviewPanelsApiState;
			open: (params: OpenParams) => void;
			close: (onClose?: () => void) => void;
			setPanelWidth: (width: number) => void;
			swap: (params: SwapParams) => void;
	  }
	| undefined
>(undefined);

const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'SET_PANEL_WIDTH':
			return { ...state, width: action.width };
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			state.panel && state?.onPreviewPanelClose?.(state.panel);
			return {
				...state,
				panel: null,
				onOpen: noop,
				onClose: noop,
			};
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel?.id === params.panel.id) {
				return state;
			}

			// Close the active panel.
			state.onClose();
			state.panel && state?.onPreviewPanelClose?.(state.panel);

			// Open the new panel.
			params.onOpen?.();
			state?.onPreviewPanelOpen?.(params.panel);
			return {
				...state,
				panel: params.panel,
				width: params.width ?? state.width,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		case 'SWAP':
			/*
				Action to swap the current panel with a new one without calling close.
				This action should be called when opening panels that are of the same type/ari
				ex: A Goals panel is already open, and user tries to open a different Goals panel.
			*/
			const { params: actionParams } = action;

			// Call action to be completed during transition.
			actionParams.onSwap?.();

			// Open the new panel.
			actionParams.onOpen?.();
			state?.onPreviewPanelOpen?.(actionParams.panel);
			return {
				...state,
				panel: actionParams.panel,
				width: actionParams.width ?? state.width,
				onOpen: actionParams.onOpen || initialState.onOpen,
				onClose: actionParams.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	onPreviewPanelOpen,
	onPreviewPanelClose,
	children,
}: {
	children: ReactNode;
	onPreviewPanelOpen?: (panel: Panel) => void;
	onPreviewPanelClose?: (panel: Panel) => void;
}) => {
	const [state, dispatch] = useReducer(previewPanelsReducer, {
		...initialState,
		onPreviewPanelOpen,
		onPreviewPanelClose,
	});

	const setPanelWidth = useCallback(
		(width: number) => dispatch?.({ type: 'SET_PANEL_WIDTH', width }),
		[dispatch],
	);

	const close = useCallback(
		(onClose?: () => void) => dispatch?.({ type: 'CLOSE', onClose }),
		[dispatch],
	);

	const open = useCallback(
		(params: OpenParams) => dispatch?.({ type: 'OPEN', params }),
		[dispatch],
	);

	const swap = useCallback(
		(params: OpenParams) => {
			if (doPreviewPanelTypesMatch(state.panel, params.panel)) {
				dispatch?.({ type: 'SWAP', params })
			} else {
				dispatch?.({ type: 'OPEN', params })
			}
		}, [dispatch, state.panel]);

	const context = useMemo(
		() => ({
			state,
			open,
			close,
			setPanelWidth,
			swap,
		}),
		[state, open, close, setPanelWidth, swap],
	);

	return <PreviewPanelsContext.Provider value={context}>{children}</PreviewPanelsContext.Provider>;
};

// Hook to use the context
export const usePreviewPanelsContext = () => {
	const context = useContext(PreviewPanelsContext);
	return context;
};
